<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAdminStore } from './stores/admin';
import Overview from './components/Overview.vue';
import ProductManagement from './components/ProductManagement.vue';
import OrderManagement from './components/OrderManagement.vue';
import StoreSettings from './components/StoreSettings.vue';

const store = useAdminStore();

const menus = ["经营概览", "商品管理", "发货退款", "店铺设置"] as const;

const isSidebarCollapsed = ref(false);
const userHasToggled = ref(false);
const STORAGE_KEY = 'admin_sidebar_collapsed';

function handleResize() {
  if (userHasToggled.value) return;
  isSidebarCollapsed.value = window.innerWidth < 1440;
}

function toggleSidebar() {
  userHasToggled.value = true;
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
  try {
    localStorage.setItem(STORAGE_KEY, isSidebarCollapsed.value ? 'true' : 'false');
  } catch {}
}

const heading = computed(() => {
  const map: Record<string, string> = {
    "经营概览": "今天的经营概览",
    "商品管理": "海货商品摆上架",
    "发货退款": "订单打包发货、运费与售后",
    "店铺设置": "门店信息与分类配置"
  };
  return map[store.activeMenu] ?? "";
});

function handleNotificationClick() {
  const hasRefund = store.unreadOrderEvents.some((e) => e.eventType === "REFUND_REVIEW_REQUESTED" || e.eventType === "REFUND_REVIEW_URGENT");
  store.targetOrderTab = hasRefund ? "REFUND" : "PENDING";
  store.selectMenu("发货退款");
  store.clearUnreadOrderEvents();
}

onMounted(() => {
  store.startSessionMonitor();
  void store.refreshCurrent();

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      userHasToggled.value = true;
      isSidebarCollapsed.value = saved === 'true';
    } else {
      isSidebarCollapsed.value = window.innerWidth < 1440;
    }
  } catch {
    isSidebarCollapsed.value = window.innerWidth < 1440;
  }
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  store.stopSessionMonitor();
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <div class="admin-shell">
    <!-- Topbar Header -->
    <header class="topbar">
      <div class="brand-lockup">
        <img src="/brand/logo.jpg" alt="码头海鲜超市" />
        <div>
          <strong>码头海鲜超市</strong>
          <span>商家经营管理后台</span>
        </div>
      </div>

      <div class="topbar-meta">
        <span class="status-dot" :class="{ active: store.apiReady }"></span>
        {{ store.apiReady ? '服务连接正常' : '未连接 API' }}
        <small class="notification-status">后台提醒：{{ store.notificationStatus.backoffice === 'READY' ? '正常' : '不可用' }} · 微信订阅：{{ store.notificationStatus.status === 'NOT_CONFIGURED' ? '未配置' : store.notificationStatus.status }}</small>
        <button v-if="store.adminTokenVerified" type="button" class="desktop-notification-button" @click="store.enableDesktopNotifications">启用桌面提醒</button>
      </div>

      <button v-if="store.adminTokenVerified && store.unreadOrderEvents.length" type="button" class="notification-chip" @click="handleNotificationClick">
        待处理消息 {{ store.unreadOrderEvents.length }} 条
      </button>

      <div class="token-field">
      <template v-if="store.adminTokenVerified">
          <span>已登录</span>
          <button type="button" class="token-login logout" @click="store.logoutAdmin">退出登录</button>
      </template>
      <template v-else-if="store.adminSessionChecking">
        <span>验证中</span>
        <small class="token-status unverified">正在检查登录状态</small>
      </template>
      <template v-else>
        <span>管理口令:</span>
        <div class="token-input-row">
          <input
            v-model="store.adminPasscode"
            @input="store.onTokenInput"
            @keyup.enter="store.loginAdmin"
            type="password"
            autocomplete="off"
            placeholder="输入口令登录"
          />
          <button
            type="button"
            class="token-login"
            :disabled="store.adminLoginLoading"
            @click="store.loginAdmin"
          >
            {{ store.adminLoginLoading ? '验证中…' : '登录' }}
          </button>
        </div>
        <small class="token-status unverified">未登录</small>
      </template>
      </div>
    </header>

    <!-- Main Workspace -->
    <div class="workspace">
      <!-- Sidebar Navigation -->
      <aside :class="['sidebar', { collapsed: isSidebarCollapsed }]">
        <div class="sidebar-header">
          <p v-show="!isSidebarCollapsed" class="eyebrow">管理菜单</p>
          <button
            type="button"
            class="sidebar-collapse-toggle"
            :title="isSidebarCollapsed ? '展开侧边栏' : '收起侧边栏'"
            @click="toggleSidebar"
          >
            <svg v-if="isSidebarCollapsed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        </div>

        <nav class="sidebar-nav">
          <el-tooltip
            v-for="menu in menus"
            :key="menu"
            :disabled="!isSidebarCollapsed"
            :content="menu"
            placement="right"
            :offset="12"
          >
            <button
              :class="['nav-item', { active: store.activeMenu === menu }]"
              @click="store.selectMenu(menu)"
            >
              <span class="nav-icon-wrap">
                <svg v-if="menu === '经营概览'" class="nav-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="7" height="9" rx="1"/>
                  <rect x="14" y="3" width="7" height="5" rx="1"/>
                  <rect x="14" y="12" width="7" height="9" rx="1"/>
                  <rect x="3" y="16" width="7" height="5" rx="1"/>
                </svg>
                <svg v-else-if="menu === '商品管理'" class="nav-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
                  <path d="m3.3 7 8.7 5 8.7-5"/>
                  <path d="M12 22V12"/>
                </svg>
                <svg v-else-if="menu === '发货退款'" class="nav-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="1" y="3" width="15" height="13" rx="2"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
                <svg v-else-if="menu === '店铺设置'" class="nav-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <span v-if="isSidebarCollapsed && menu === '发货退款' && store.pendingTasksCount > 0" class="collapsed-dot-indicator"></span>
              </span>
              <span v-show="!isSidebarCollapsed" class="nav-label">{{ menu }}</span>
              <el-badge
                v-if="!isSidebarCollapsed && menu === '发货退款' && store.pendingTasksCount > 0"
                :value="store.pendingTasksCount"
                type="warning"
                class="nav-badge"
              />
            </button>
          </el-tooltip>
        </nav>
      </aside>

      <!-- Main Content Area -->
      <main class="main-content">
        <!-- Page Title & Metrics Header -->
        <section class="page-heading">
          <div>
            <p class="eyebrow">{{ store.activeMenu }}</p>
            <h1>{{ heading }}</h1>
          </div>
          <el-button type="primary" plain icon="Refresh" @click="store.refreshCurrent">
            刷新数据
          </el-button>
        </section>

        <!-- Metric Cards Bar -->
        <section class="metric-row">
          <div class="metric-card">
            <span>在售海鲜商品</span>
            <strong>{{ store.products.filter((item) => item.active).length }} 件</strong>
          </div>
          <div class="metric-card">
            <span>待处理订单</span>
            <strong :class="{ highlight: store.pendingShipments > 0 }">{{ store.pendingShipments || '0' }} 单</strong>
          </div>
          <div class="metric-card">
            <span>退款处理中</span>
            <strong :class="{ highlight: store.pendingRefunds > 0 }">{{ store.pendingRefunds || '0' }} 单</strong>
          </div>
          <div class="metric-card">
            <span>到店待核销自提</span>
            <strong>{{ store.pickupReady || '0' }} 单</strong>
          </div>
        </section>

        <!-- Notice Alert -->
        <p v-if="store.errorMessage" class="notice-alert">{{ store.errorMessage }}</p>

        <!-- Dynamic View Components -->
        <Overview v-if="store.activeMenu === '经营概览'" />
        <ProductManagement v-else-if="store.activeMenu === '商品管理'" />
        <OrderManagement v-else-if="store.activeMenu === '发货退款'" />
        <StoreSettings v-else-if="store.activeMenu === '店铺设置'" />
      </main>
    </div>
  </div>
</template>

<style scoped>
.highlight {
  color: #f56c6c !important;
}
.nav-badge {
  margin-left: auto;
}
.notice-alert {
  margin: 0 0 20px;
  padding: 12px 16px;
  border-left: 4px solid #e6a23c;
  color: #895d12;
  background: #fdf6ec;
  border-radius: 4px;
  font-size: 13px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #909399;
}
.status-dot.active {
  background: #67c23a;
  box-shadow: 0 0 0 3px #e1f3d8;
}
.desktop-notification-button {
  margin-left: 10px;
  padding: 4px 8px;
  border: 1px solid #d7e2ef;
  border-radius: 6px;
  color: #315a85;
  background: #f6f9fd;
  font-size: 12px;
  cursor: pointer;
}
.desktop-notification-button:hover {
  background: #edf4fc;
}
</style>
